import type { RouteObject } from 'react-router';

import { createPaths } from '@ricoh/ria.client.core/utils/router/path';

/** Platform route. */
const platformRoute: RouteObject = {
  lazy: async () => (await import('~/components/layout/platform')).default,

  children: [
    // Batch
    {
      path: 'batch',

      children: [
        // Batch
        {
          path: '',
          lazy: async () => (await import('~/components/page/user/platform/batch')).default,
        },

        // New Batch
        {
          path: 'new',
          index: true,
          lazy: async () => (await import('~/components/page/user/platform/batch/new')).default,
        },

        // New batch
        {
          path: `new/:id`,
          lazy: async () => (await import('~/components/page/user/platform/batch/new')).default,
        },

        // Review batch
        {
          path: `review/:id`,
          lazy: async () => (await import('~/components/page/user/platform/batch/review')).default,
        },

        // Validate batch
        {
          path: `validation/:id`,
          lazy: async () => (await import('~/components/page/user/platform/batch/validation')).default,
        },

        // Confirm batch
        {
          path: `confirmation/:id`,
          lazy: async () => (await import('~/components/page/user/platform/batch/confirmation')).default,
        },

        // Drafts List
        {
          path: 'drafts',
          lazy: async () => (await import('~/components/page/user/platform/batch/drafts')).default,
        },
      ],
    },

    // Work Queue
    {
      path: 'work-queue',

      children: [
        // Queue list
        {
          path: '',
          index: true,
          lazy: async () => (await import('~/components/page/user/platform/work-queue/queue')).default,
        },
      ],
    },

    // Performance
    {
      path: 'performance',
      lazy: async () => (await import('~/components/page/user/platform/performance')).default,
    },

    // Analytics
    {
      path: 'analytics',
      lazy: async () => (await import('~/components/page/user/platform/analytics')).default,
    },
  ],
};

/** Preferences route. */
const preferencesRoute: RouteObject = {
  path: 'preferences',
  lazy: async () => (await import('~/components/layout/preferences')).default,

  children: [
    // Profile
    {
      path: 'profile',
      index: true,
      lazy: async () => (await import('~/components/page/user/preferences/profile')).default,
    },

    // Privacy Security
    {
      path: 'privacy-security',
      lazy: async () => (await import('~/components/page/user/preferences/privacy-security')).default,
    },

    // Accessibility
    {
      path: 'accessibility',
      lazy: async () => (await import('~/components/page/user/preferences/accessibility')).default,
    },

    // Work Schedule
    {
      path: 'work-schedule',
      lazy: async () => (await import('~/components/page/user/preferences/work-schedule')).default,
    },

    // General
    {
      path: 'general',
      lazy: async () => (await import('~/components/page/user/preferences/general')).default,
    },
  ],
};

/** Settings route. */
const settingsRoute: RouteObject = {
  path: 'settings',
  lazy: async () => (await import('~/components/layout/settings')).default,

  children: [
    // Account
    {
      path: 'account',
      index: true,
      lazy: async () => (await import('~/components/page/user/settings/account')).default,
    },

    {
      path: 'processing-history',
      lazy: async () => (await import('~/components/page/user/settings/processing-history')).default,
    },

    // Services
    {
      path: 'services',
      lazy: async () => (await import('~/components/page/user/settings/services')).default,
    },

    // Users
    {
      path: 'users',
      lazy: async () => (await import('~/components/page/user/settings/users')).default,
    },

    // Groups
    {
      path: 'groups',
      lazy: async () => (await import('~/components/page/user/settings/groups')).default,
    },

    // Databases
    {
      path: 'databases',
      lazy: async () => (await import('~/components/page/user/settings/databases')).default,
    },

    //  Integrations
    {
      path: 'integrations',
      lazy: async () => (await import('~/components/page/user/settings/integrations')).default,
    },

    // Scanning Profiles
    {
      path: 'scanning-profiles',
      lazy: async () => (await import('~/components/page/user/settings/scanning-profiles')).default,
    },

    // Scanning Profiles
    {
      path: 'separation-profiles',
      lazy: async () => (await import('~/components/page/user/settings/separation-profiles')).default,
    },

    // Global Variables
    {
      path: 'global-variables',
      lazy: async () => (await import('~/components/page/user/settings/global-variables')).default,
    },

    // AI Model Library
    {
      path: 'ai-model-library',
      lazy: async () => (await import('~/components/page/user/settings/ai-model-library')).default,
    },

    // Batch Type Library
    {
      path: 'batch-type-library',
      lazy: async () => (await import('~/components/page/user/settings/batch-type-library')).default,
    },

    // Work Queue Filters
    {
      path: 'work-queue-filters',
      lazy: async () => (await import('~/components/page/user/settings/work-queue-filters')).default,
    },
  ],
};

/** Blueprint route. */
const blueprintRoute: RouteObject = {
  lazy: async () => (await import('~/components/layout/blueprint')).default,

  children: [
    // Batch details
    {
      path: 'batch/details/:id',
      lazy: async () => (await import('~/components/page/user/platform/batch/details')).default,
    },

    // Settings / Batch Type Library
    {
      path: 'settings/batch-type-library',

      children: [
        // Process Flow
        {
          path: 'process-flow',
          lazy: async () => (await import('~/components/page/user/settings/batch-type-library/process-flow')).default,
        },

        // Document layout
        {
          path: 'document-layout',
          lazy: async () => (await import('~/components/page/user/settings/batch-type-library/document-layout')).default,
        },
      ],
    },
  ],
};

/** Authenticated User route. */
const userRoute: RouteObject = {
  children: [platformRoute, preferencesRoute, settingsRoute, blueprintRoute],
};

/** User paths. */
export const userPaths = createPaths(userRoute);

/** User path regexes. */
export const userPathRegexes = {
  platform: {
    batch: /^\/batch/,
    workQueue: /^\/work-queue/,
    performance: /^\/performance/,
    analytics: /^\/analytics/,
  },

  preferences: {
    profile: /^\/preferences\/profile/,
    privacySecurity: /^\/preferences\/privacy-security/,
    accessibility: /^\/preferences\/accessibility/,
    workSchedule: /^\/preferences\/work-schedule/,
    general: /^\/preferences\/general/,
  },

  settings: {
    account: /^\/settings\/account/,
    processingHistory: /^\/settings\/processing-history/,
    services: /^\/settings\/services/,
    users: /^\/settings\/users/,
    groups: /^\/settings\/groups/,
    databases: /^\/settings\/databases/,
    integrations: /^\/settings\/integrations/,
    scanningProfiles: /^\/settings\/scanning-profiles/,
    separationProfiles: /^\/settings\/separation-profiles/,
    globalVariables: /^\/settings\/global-variables/,
    aiModelLibrary: /^\/settings\/ai-model-library/,
    batchTypeLibrary: /^\/settings\/batch-type-library/,
    workQueueFilters: /^\/settings\/work-queue-filters/,
  },
};

export default userRoute;
